import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { LessonFormData, QuizQuestion } from "@/components/admin/lesson/types";
import { toast } from "@/components/ui/use-toast";
import { updateLessonQuestions } from "@/utils/quizOperations";

export const useLesson = (lessonId: string | undefined) => {
  const getLesson = async () => {
    try {
      if (!lessonId) {
        throw new Error('No lesson ID provided');
      }

      console.log('Fetching lesson:', lessonId);

      // 1. Obtener la lección
      const { data: lesson, error: lessonError } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', lessonId)
        .single();

      if (lessonError) {
        console.error('Error fetching lesson:', lessonError);
        throw lessonError;
      }

      if (!lesson) {
        console.error('No lesson found with ID:', lessonId);
        throw new Error('Lesson not found');
      }

      console.log('Lesson found:', lesson);

      // 2. Obtener las preguntas de la lección
      const { data: questions, error: questionsError } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('lesson_id', lessonId)
        .order('created_at', { ascending: true });

      if (questionsError) {
        console.error('Error fetching questions:', questionsError);
        throw questionsError;
      }

      console.log('Quiz questions found:', questions);

      // Formatear las preguntas para el formulario
      const formattedQuestions = questions?.map(q => ({
        id: q.id,
        question: q.question || '',
        correct_answer: q.correct_answer || '',
        wrong_answers: q.wrong_answers && Array.isArray(q.wrong_answers) 
          ? q.wrong_answers.filter(answer => answer && answer.trim() !== '')
          : [],
        points: q.points || 1
      })) || [];

      console.log('Formatted questions:', formattedQuestions);

      // Formatear los datos de la lección para el formulario
      const formattedLesson: LessonFormData = {
        title: lesson.title || '',
        video_url: lesson.video_url || '',
        audio_url: lesson.audio_url || '',
        content_text: lesson.content_text || '',
        level: lesson.level || 'principiante',
        published: lesson.published || false,
        is_premium: lesson.is_premium || false,
        quiz_questions: formattedQuestions,
      };

      return formattedLesson;
    } catch (error: any) {
      console.error('Error in getLesson:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: getLesson,
    enabled: !!lessonId,
    retry: false,
    onError: (error: any) => {
      console.error('Query error:', error);
      toast({
        title: "Error",
        description: error.message || "Error al cargar la lección",
        variant: "destructive",
      });
    }
  });
};

export const updateLesson = async (
  lessonId: string, 
  data: LessonFormData,
  queryClient: any
) => {
  try {
    console.log('Updating lesson with data:', data);

    // Función auxiliar para normalizar URLs
    const normalizeUrl = (url: string | undefined) => {
      if (!url) return '';
      // Eliminar cualquier protocolo existente
      const cleanUrl = url.replace(/^https?:\/\//, '');
      // Asegurarse de que no haya dobles barras
      return cleanUrl.replace(/\/\//g, '/');
    };

    const lessonUpdate = {
      title: data.title?.trim(),
      video_url: normalizeUrl(data.video_url),
      audio_url: normalizeUrl(data.audio_url),
      content_text: data.content_text?.trim(),
      level: data.level || 'principiante',
      published: data.published || false,
      is_premium: data.is_premium || false,
    };

    console.log('Updating lesson with:', lessonUpdate);

    const { error: lessonError } = await supabase
      .from('lessons')
      .update(lessonUpdate)
      .eq('id', lessonId);

    if (lessonError) throw lessonError;

    // Actualizar las preguntas del quiz
    if (data.quiz_questions) {
      console.log('Updating quiz questions:', data.quiz_questions);
      await updateLessonQuestions(lessonId, data.quiz_questions);
    }

    // Invalidar la caché de la lección
    await queryClient.invalidateQueries(['lesson', lessonId]);
    await queryClient.invalidateQueries(['admin-lessons']);

    return true;
  } catch (error) {
    console.error('Error updating lesson:', error);
    throw error;
  }
};